import React, { ReactElement, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import { Box, Button, Typography} from "@mui/material";
import { colors } from "../../../common/colors";
import { convertHeicToJpeg, getFileExtenction } from "../../../common/files";
import { imageAcceptedExtension, validateImage } from "../../../common/images";
import apiServices from "../../services/apiServices";
import { formatNumber, isAdmin } from "../../util/util";
import CreateUpdateButton from "../CreateUpdateButton";
import FormDialog from "../FormDialog";

interface Props {
  fileInfo?: any
  onSubmit: (file: File, orgFileInfo?: any) => Promise<boolean>;
  onClose: () => void;
  onError: (message: string) => void;
}

type Image = {
  file: File
  filename: string
  size: number
}

function ImageDialog({ fileInfo, onSubmit, onClose, onError }: Props): ReactElement {
  const { admin } = useSelector((state: any) => state.adminAuth);

  const [image, setImage] = useState<Image>();
  const [error, setError] = useState<string | null>();

  useEffect(() => {
    if (!fileInfo?.id) return

    const getSampleImage = async () => {
      try {
        const file = await apiServices.getSampleImage(fileInfo.id);
        setImage({ file: file, filename: fileInfo.name, size: fileInfo.size });
      } catch {
        onError(T("mng_sample_images_update_error"));
      }
    }
    getSampleImage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileInfo]);

  /* API */
  const handleSubmit = async() => {
    if (!image) return false;

    const ret = onSubmit(image.file, fileInfo);
    return ret;
  }

  /* イベント */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleImageUpload(file, e);
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }

  const handleImageUpload = (file: File | null, event: any) => {
    if (!isAdmin(admin) || fileInfo?.is_used) return;

    if (!file) {
      console.error("ファイルを取得できませんでした。", event);
      return
    }

    const errMsg = validateImage(file);
    if (errMsg) {
      setError(errMsg);
      return
    }

    setError(null);

    // 選択されたファイルをセット
    const newImages = [file].map((file: any) => {
      if (getFileExtenction(file) === "heic") {
        return new Promise(async (resolve, reject) => {
          convertHeicToJpeg(file)
            .then((f: File) => resolve(f))
            .catch((e: any) => reject(e))
        })
      } else {
        let reader = new FileReader();
        return new Promise((resolve) => {
          reader.onload = (e: any) => {
            resolve(file);
          };
          reader.readAsDataURL(file);
        });
      }
    });

    Promise.all(newImages).then((loadedImages) => {
      const _file = loadedImages[0] as File;
      setImage({
        file: _file,
        filename: _file.name,
        size: _file.size
      });
    }).catch((e: any) => console.error(e));
  }

  return (
    <FormDialog
      fullWidth slotProps={{ backdrop: { sx:styles.backdrop } }}
      open={true}
      onClose={onClose}
    >
      <Box sx={styles.container}>
        <Box onDrop={handleDrop} onDragOver={handleDragOver} sx={styles.imageContainer}>
          {image ? (
            <img src={URL.createObjectURL(image.file)} alt={T("mng_sample_images_alt")} />
          ) : (
            <Box component="div">
              {T("mng_sample_images_drag_and_drop")}
            </Box>
          )}
        </Box>
        {error && <Typography sx={styles.error}>{ error }</Typography>}
        {isAdmin(admin) && !fileInfo?.is_used && 
          <Button component="label" variant="outlined" sx={styles.selectButton}>
            {T("mng_sample_images_select_file")}
            <input
              type="file"
              hidden
              accept={imageAcceptedExtension}
              onChange={(e: any) => handleImageUpload(e.target.files?.[0] ?? null, e)}
            />
          </Button>
        }
        <Box sx={styles.fileInfo}>
          <Typography>{image?.filename}</Typography>
          <Typography>{image ? `${formatNumber(image.size / 1024)}KB` : ""}</Typography>
        </Box>
        {isAdmin(admin) && !fileInfo?.is_used && 
          <CreateUpdateButton
            title={fileInfo ? T('mng_sample_images_update') : T('mng_sample_images_create')}
            disabled={!image}
            onSubmit={handleSubmit}
            validate={() => Promise.resolve(true)}
          />
        }
      </Box>
    </FormDialog>
  );
};
export default ImageDialog;

const styles = {
  backdrop: {
    backgroundColor: "rgba(0, 0, 0, 0.51)" // 白い線が出てしまう対策
  },
  container: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 1,
    marginY: 2,
  },
  imageContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    cursor: "pointer",
    position: "relative",
    margin: "auto",
    "& img": {
      maxWidth: "500px",
      minWidth: "500px",
      border: `1px dashed ${colors.farm_gray200}`,
    },
    "& div": {
      width: "500px",
      height: "500px",
      border: `1px dashed ${colors.farm_gray200}`,
      lineHeight: "500px",
      textAlign: "center",
    }
  },
  error: {
    color: colors.farm_red600,
    textAlign: "center"
  },
  selectButton: {
    width: "fit-content",
    margin: "auto",
  },
  fileInfo: {
    minHeight: "2em",
    textAlign: "center",
  }
}
